import { randomInt } from 'crypto';
import { TythonColumnsEnum } from '../../@data/store/schema/ColumnsEnum';
import { ICustomCalenderDateRange } from '../../components/CustomCalender/CustomCalender.types';
import {
  DISTRIBUTION_SECTION_ID,
  IReportItem,
  IReportSection,
  SYNDICATION_SECTION_ID,
} from '../../components/LeftHandNavigation/LeftHandNavigation.types';
import { IPubCenterMetadataElement, PubCenterReportingDataRow } from '../../DashboardCards/@data/store/schema';
import { ChartGrain } from '../../DashboardCards/ReportCard/ReportCard.types';
import { downloadFile } from '../../utils/downloadFile';

export const CLARITY_REPORT_NAME = 'TelemetrySignalCoverage';
const MILLIS_IN_A_DAY = 1000 * 3600 * 24;

export const ReportNamesMapping = {
  account: 'Account Performance',
  site: 'Site Performance',
  adunit: 'Ad unit performance',
  productad: 'Product ad performance',
  typetag: 'Type tag performance',
  ptqs: 'PTQS',
  adunitaudit: 'Ad unit audit',
  adunitsnapshot: 'Ad unit snapshot',
  distributionptqssnapshot: 'Distribution PTQS Snapshot',
  ptqssnapshot: 'PTQS Snapshot',
  distributionpartnertagv2: 'Partner Tag V2 performance',
  distributionpageloadperf: 'Page Load performance',
  distributionptqs: 'Distribution PTQS',
  distributionv2: 'Distribution V2 performance',
  distribution: 'Distribution performance',
  startdistributioncontent: 'Start Distribution Content',
  startdistributionnativeadsinfeedsandheadlines: 'Start Distribution Native Ads In Feeds And Headlines',
  clarityReport: CLARITY_REPORT_NAME,
  // add other report types as needed
};

export const filterNameToPayloadKeyFunc = (reportType?: string) => {
  const filterNameToPayloadKey: Record<string, string> = {
    Account: 'AccountId',
    Site: 'SiteId',
    'Account | Ad Unit': 'AdUnitId',
    'Account | Partner Code': reportType === 'Distribution' ? 'DistributionPartnerCode' : 'PartnerCode',
    'Eligibility Status': 'selectTierEligibilityStatus',
    'Eligibility downgrade reason': 'selectTierDowngradeReason',
    'Partner Tag': 'PartnerTag',
    'Form Code': 'FormCode',
    'Type Tag': 'TypeTag',
    'Ad Type': 'AdType',
    AdDistribution: 'AdDistribution',
    Countries: 'MarketCountry',
    Languages: 'MarketLanguage',
    'Content Experiences': 'ContentExperience',
    Experiences: 'Experience',
    'Website Country': 'WebsiteCountryCode',
    'Device Type': 'DeviceType',
    'Clarity Project ID': 'ProjectId',
    // ...other mappings
  };

  return filterNameToPayloadKey;
};

export const filterKeyToDisplayName: Record<string, string> = {
  AccountId: 'Account',
  SiteId: 'Site',
  AdUnitId: 'Account | Ad Unit',
  DistributionPartnerCode: 'Account | Partner Code',
  PartnerCode: 'Account | Partner Code',
  selectTierEligibilityStatus: 'Eligibility Status',
  selectTierDowngradeReason: 'Eligibility downgrade reason',
  PartnerTag: 'Partner Tag',
  FormCode: 'Form Code',
  TypeTag: 'Type Tag',
  AdType: 'Ad Type',
  AdDistribution: 'AdDistribution',
  MarketCountry: 'Countries',
  MarketLanguage: 'Languages',
  ContentExperience: 'Content Experiences',
  Experience: 'Experiences',
  WebsiteCountryCode: 'Website Country',
  DeviceType: 'Device Type',
  ProjectId: 'Clarity Project ID',
  // ...other mappings
};

const COLUMNS_AVERAGED = [
  TythonColumnsEnum.CTR,
  TythonColumnsEnum.CPC,
  TythonColumnsEnum.AdCTR,
  TythonColumnsEnum.eCPM,
  TythonColumnsEnum.AdECPM,
];

export function aggregateReportDataByField(
  reportData: PubCenterReportingDataRow[] | undefined,
  groupByField: string
): PubCenterReportingDataRow[] | undefined {
  const aggregated: { [key: string]: PubCenterReportingDataRow } = {};

  if (!reportData) {
    return undefined;
  }

  reportData.forEach((row) => {
    const groupKey = row[groupByField];
    if (!groupKey) {
      return;
    }

    if (!aggregated[groupKey]) {
      // Clone the row to start aggregation
      aggregated[groupKey] = { ...row };
      // Remove non-numeric, non-group fields
      Object.keys(row).forEach((key) => {
        if (key !== groupByField && typeof row[key] !== 'number') {
          delete aggregated[groupKey][key];
        }
      });
    } else {
      // Aggregate numeric fields
      Object.keys(row).forEach((key) => {
        if (key !== groupByField && typeof row[key] === 'number') {
          aggregated[groupKey][key] = (aggregated[groupKey][key] || 0) + (row[key] as number);
        }
      });
    }
  });

  Object.keys(aggregated).forEach((key) => {
    Object.keys(aggregated[key]).forEach((metricKey) => {
      if (typeof aggregated[key][metricKey] === 'number' && COLUMNS_AVERAGED.includes(metricKey as TythonColumnsEnum)) {
        aggregated[key][metricKey] = (aggregated[key][metricKey] * Object.values(aggregated).length) / reportData.length;
      }
    });
  });

  return Object.values(aggregated);
}

export function aggregateReportData(reportData: PubCenterReportingDataRow[] | undefined): PubCenterReportingDataRow[] | undefined {
  if (!reportData || reportData.length === 0) {
    return undefined;
  }

  const result: PubCenterReportingDataRow = {} as PubCenterReportingDataRow;

  Object.keys(reportData[0]).forEach((key) => {
    if (typeof reportData[0][key] === 'number') {
      result[key] = 0;
    }
  });

  reportData.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (typeof row[key] === 'number') {
        result[key] = (result[key] || 0) + (row[key] as number);
      }
    });
  });

  // Average the fields that should be averaged
  COLUMNS_AVERAGED.forEach((key) => {
    if (typeof result[key] === 'number') {
      result[key] = result[key] / reportData.length;
    }
  });

  return [result];
}

export function aggregateByField(data: PubCenterReportingDataRow[], field: string): PubCenterReportingDataRow[] {
  const result: { [key: string]: PubCenterReportingDataRow } = {};

  data.forEach((row) => {
    const key = row[field];
    if (!result[key]) {
      result[key] = { ...row };
    } else {
      Object.keys(row).forEach((k) => {
        if (k !== field && typeof row[k] === 'number') {
          result[key][k] = ((result[key][k] as number) || 0) + (row[k] as number);
        }
      });
    }
  });

  return Object.values(result);
}

export const downloadReport = (downloadURL: string | undefined, reportName: string, isCSV: boolean) => {
  if (downloadURL && isCSV) {
    downloadFile(downloadURL, reportName);
  } else {
    // Download as PNG (implement if needed)
  }
};

export const isGrainWithinDateRange = (dateRange: ICustomCalenderDateRange, grain?: ChartGrain, maxDateDiff?: number) => {
  // Uncomment and implement the logic if needed
  const { startDate, endDate } = dateRange;
  if (startDate > endDate) {
    return false;
  }
  const nowDate = new Date();
  const dateDifferenceInDays = (endDate.getTime() - startDate.getTime()) / MILLIS_IN_A_DAY;
  const diffInDaysBetweenNowAndStartDate = (nowDate.getTime() - startDate.getTime()) / MILLIS_IN_A_DAY;
  switch (grain) {
    case ChartGrain.HOURLY:
      return dateDifferenceInDays <= 40;
    case ChartGrain.DAILY:
      return dateDifferenceInDays <= 180 && diffInDaysBetweenNowAndStartDate >= 1;
    case ChartGrain.WEEKLY:
      const dayOfWeek = startDate.getDay();
      const adjustedStartDate = new Date(startDate);
      if (dayOfWeek !== 0) {
        adjustedStartDate.setDate(startDate.getDate() + (7 - dayOfWeek));
      }
      const adjustedDateDifferenceInDays = (endDate.getTime() - adjustedStartDate.getTime()) / MILLIS_IN_A_DAY;
      return adjustedDateDifferenceInDays >= 6 && adjustedDateDifferenceInDays <= 180;
    case ChartGrain.MONTHLY:
      const dayOfMonth = startDate.getDate();
      const adjustedStartDateForMonth = new Date(startDate);
      if (dayOfMonth !== 1) {
        adjustedStartDateForMonth.setMonth(startDate.getMonth() + 1);
        adjustedStartDateForMonth.setDate(1);
      }
      const daysInMonth = new Date(adjustedStartDateForMonth.getFullYear(), adjustedStartDateForMonth.getMonth() + 1, 0).getDate();
      const adjustedDateDifferenceInDaysForMonth = (endDate.getTime() - adjustedStartDateForMonth.getTime()) / MILLIS_IN_A_DAY;
      return (
        adjustedDateDifferenceInDaysForMonth >= daysInMonth - 1 &&
        adjustedDateDifferenceInDaysForMonth <= daysInMonth * 60 &&
        adjustedDateDifferenceInDaysForMonth <= (maxDateDiff || Infinity)
      );
    case ChartGrain.YEARLY:
      const yearStartDate = new Date(startDate.getFullYear(), 0, 1);
      const dayOfYear = (yearStartDate.getTime() - startDate.getTime()) / MILLIS_IN_A_DAY;
      const adjustedStartDateForYear = new Date(startDate);
      if (dayOfYear !== 0) {
        adjustedStartDateForYear.setFullYear(startDate.getFullYear() + 1);
        adjustedStartDateForYear.setMonth(0);
        adjustedStartDateForYear.setDate(1);
      }
      const daysInYear =
        (adjustedStartDateForYear.getFullYear() % 4 === 0 && adjustedStartDateForYear.getFullYear() % 100 !== 0) ||
        adjustedStartDateForYear.getFullYear() % 400 === 0
          ? 366
          : 365;
      const adjustedDateDifferenceInDaysForYear = (endDate.getTime() - adjustedStartDateForYear.getTime()) / MILLIS_IN_A_DAY;
      return adjustedDateDifferenceInDaysForYear >= daysInYear - 1;
    default:
      return false;
  }
};

export const getDefaultMetrics = (reportType: string): TythonColumnsEnum[] => {
  const isDistributionV2Report = reportType.toLowerCase() === 'distributionv2' || reportType.toLowerCase() === 'distributionpartnertagv2';
  const isStartDistributionReport = reportType.toLowerCase().includes('startdistribution');
  return isStartDistributionReport
    ? [TythonColumnsEnum.AdImpressions, TythonColumnsEnum.NetRevenue, TythonColumnsEnum.PageViews, TythonColumnsEnum.UniqueUsers]
    : reportType.toLowerCase() in defaultMetricsByReportType
    ? defaultMetricsByReportType[reportType.toLowerCase()]
    : [
        TythonColumnsEnum.BiddedSRPVs,
        TythonColumnsEnum.Clicks,
        isDistributionV2Report ? TythonColumnsEnum.Revenue : TythonColumnsEnum.EstimatedRevenue,
      ];
};

export const getIsPremadeReport = (report?: IReportItem, reports?: IReportSection[]) => {
  const reportSectionId = reports?.find((s) => s.items.some((i) => i.id === report?.id))?.id;
  return reportSectionId === SYNDICATION_SECTION_ID || reportSectionId === DISTRIBUTION_SECTION_ID;
};

export const generateNewName = (name: string, names: string[], wordGeneration: string) => {
  const newName = name + ' ' + wordGeneration;
  if (!names.includes(newName)) {
    return newName;
  }
  for (let i = 1; i < 100; i++) {
    if (!names.includes(newName + ' ' + i)) {
      return newName + ' ' + i;
    }
  }
  return newName + ' ' + randomInt(100, 1000000);
};

const defaultMetricsByReportType: Record<string, TythonColumnsEnum[]> = {
  distributionpageloadperf: [
    TythonColumnsEnum.P90_Latency_ms,
    TythonColumnsEnum.P50_Latency_ms,
    TythonColumnsEnum.P50_Latency_ms_7DMA,
    TythonColumnsEnum.P90_Latency_ms_7DMA,
  ],
};

export const AdDistributionFilter: IPubCenterMetadataElement[] = [
  {
    type: 'AdDistribution',
    id: 'PaidSearch',
    name: 'Paid Search',
    additionalFields: {},
  },
  {
    type: 'AdDistribution',
    id: 'Native',
    name: 'Native',
    additionalFields: {},
  },
];

export const AdTypesFilter: IPubCenterMetadataElement[] = [
  {
    type: 'AdType',
    id: 'TextAd',
    name: 'Text Ad',
    additionalFields: {},
  },
  {
    type: 'AdType',
    id: 'ProductAd',
    name: 'Product Ad',
    additionalFields: {},
  },
  {
    type: 'AdType',
    id: 'Other',
    name: 'Other',
    additionalFields: {},
  },
];

// --- Config: Report type to dimension requests ---
export const reportTypeDimensionConfig: Record<string, Array<{ filter: string; hierarchy?: string; filterDisplayName: string }>> = {
  distribution: [
    { filter: 'FormCode', filterDisplayName: 'Form Code' },
    { filter: 'Account', hierarchy: 'PartnerCode', filterDisplayName: 'Account | Partner Code' },
  ],
  distributionv2: [
    { filter: 'FormCode', filterDisplayName: 'Form Code' },
    { filter: 'Account', hierarchy: 'PartnerCode', filterDisplayName: 'Account | Partner Code' },
  ],
  distributionptqs: [
    { filter: 'Account', hierarchy: 'PartnerCode', filterDisplayName: 'Account | Partner Code' },
    { filter: 'PartnerTag', filterDisplayName: 'Partner Tag' },
  ],
  distributionpageloadperf: [{ filter: 'Account', hierarchy: 'PartnerCode', filterDisplayName: 'Account | Partner Code' }],
  distributionpartnertagv2: [
    { filter: 'FormCode', filterDisplayName: 'Form Code' },
    { filter: 'Account', hierarchy: 'PartnerCode', filterDisplayName: 'Account | Partner Code' },
    { filter: 'PartnerTag', filterDisplayName: 'Partner Tag' },
  ],
  account: [{ filter: 'Account', filterDisplayName: 'Account' }],
  adunit: [{ filter: 'Account', hierarchy: 'AdUnit', filterDisplayName: 'Account | Ad Unit' }],
  site: [
    { filter: 'Account', filterDisplayName: 'Account' },
    { filter: 'Site', filterDisplayName: 'Site' },
  ],
  productad: [{ filter: 'Account', hierarchy: 'AdUnit', filterDisplayName: 'Account | Ad Unit' }],
  typetag: [
    { filter: 'Account', hierarchy: 'AdUnit', filterDisplayName: 'Account | Ad Unit' },
    { filter: 'TypeTag', filterDisplayName: 'Type Tag' },
    { filter: 'AdType', filterDisplayName: 'Ad Type' },
  ],
  ptqs: [
    { filter: 'Account', hierarchy: 'AdUnit', filterDisplayName: 'Account | Ad Unit' },
    { filter: 'TypeTag', filterDisplayName: 'Type Tag' },
  ],
  adunitaudit: [
    { filter: 'Account', hierarchy: 'AdUnit', filterDisplayName: 'Account | Ad Unit' },
    { filter: 'SelectStatus', filterDisplayName: 'Eligibility Status' },
    { filter: 'downgradereason', filterDisplayName: 'Eligibility downgrade reason' },
  ],
  adunitsnapshot: [
    { filter: 'Account', hierarchy: 'AdUnit', filterDisplayName: 'Account | Ad Unit' },
    { filter: 'SelectStatus', filterDisplayName: 'Eligibility Status' },
    { filter: 'downgradereason', filterDisplayName: 'Eligibility downgrade reason' },
  ],
  distributionptqssnapshot: [{ filter: 'Account', hierarchy: 'PartnerCode', filterDisplayName: 'Account | Partner Code' }],
  ptqssnapshot: [{ filter: 'Account', hierarchy: 'AdUnit', filterDisplayName: 'Account | Ad Unit' }],
  startdistributioncontent: [
    { filter: 'ContentExperience', filterDisplayName: 'Content Experiences' },
    { filter: 'MarketLanguage', filterDisplayName: 'Languages' },
    { filter: 'MarketCountry', filterDisplayName: 'Countries' },
  ],
  startdistributionnativeadsinfeedsandheadlines: [{ filter: 'Experience', filterDisplayName: 'Experiences' }],
  [CLARITY_REPORT_NAME.toLowerCase()]: [
    { filter: 'Account', hierarchy: 'AdUnit', filterDisplayName: 'Account | Ad Unit' },
    { filter: 'Site', filterDisplayName: 'Site' },
    { filter: 'DeviceType', filterDisplayName: 'Device Type' },
    { filter: 'WebsiteCountry', filterDisplayName: 'Website Country' },
    { filter: 'ProjectId', filterDisplayName: 'Clarity Project ID' },
  ],
};

export const reportTypesWithNoChart: Set<string> = new Set<string>([
  'AdUnitSnapshot',
  'AdunitAudit',
  'DistributionPTQSSnapshot',
  'PTQSSnapshot',
  'DistributionPTQS',
  CLARITY_REPORT_NAME,
]);

export const reportTypesWithNoGranularity: Set<string> = new Set<string>([
  'AdUnitSnapshot',
  'AdunitAudit',
  'PTQSSnapshot',
  'DistributionPTQSSnapshot',
]);

export const reportTypeDefaultColumns: Record<string, TythonColumnsEnum[]> = {
  account: [
    TythonColumnsEnum.AccountName,
    TythonColumnsEnum.BiddedSRPVs,
    TythonColumnsEnum.Clicks,
    TythonColumnsEnum.BiddedCTR,
    TythonColumnsEnum.CPC,
    TythonColumnsEnum.EstimatedRevenue,
    TythonColumnsEnum.BiddedRPM,
  ],
  site: [
    TythonColumnsEnum.SiteName,
    TythonColumnsEnum.SiteUrl,
    TythonColumnsEnum.BiddedSRPVs,
    TythonColumnsEnum.Clicks,
    TythonColumnsEnum.BiddedCTR,
    TythonColumnsEnum.CPC,
    TythonColumnsEnum.EstimatedRevenue,
    TythonColumnsEnum.BiddedRPM,
  ],
  adunit: [
    TythonColumnsEnum.AdUnitName,
    TythonColumnsEnum.RawSRPVs,
    TythonColumnsEnum.BiddedSRPVs,
    TythonColumnsEnum.Coverage,
    TythonColumnsEnum.AdImpressions,
    TythonColumnsEnum.BiddedRPM,
    TythonColumnsEnum.Clicks,
    TythonColumnsEnum.BiddedCTR,
    TythonColumnsEnum.EstimatedRevenue,
    TythonColumnsEnum.CPC,
    TythonColumnsEnum.RPM,
  ],
  productad: [
    TythonColumnsEnum.AdUnitName,
    TythonColumnsEnum.BiddedSRPVs,
    TythonColumnsEnum.AdImpressions,
    TythonColumnsEnum.Clicks,
    TythonColumnsEnum.BiddedCTR,
    TythonColumnsEnum.EstimatedRevenue,
    TythonColumnsEnum.CPC,
    TythonColumnsEnum.BiddedRPM,
  ],
  typetag: [
    TythonColumnsEnum.TypeTag,
    TythonColumnsEnum.RawSRPVs,
    TythonColumnsEnum.BiddedSRPVs,
    TythonColumnsEnum.Coverage,
    TythonColumnsEnum.AdImpressions,
    TythonColumnsEnum.Clicks,
    TythonColumnsEnum.BiddedCTR,
    TythonColumnsEnum.EstimatedRevenue,
    TythonColumnsEnum.CPC,
    TythonColumnsEnum.RPM,
  ],
  adunitaudit: [
    TythonColumnsEnum.ModifiedDate,
    TythonColumnsEnum.AdUnitId,
    TythonColumnsEnum.AdUnitName,
    TythonColumnsEnum.Action,
    TythonColumnsEnum.SelectTierEligibilityStatus,
    TythonColumnsEnum.PreviousSelectTierEligibilityStatus,
    TythonColumnsEnum.SelectTierDowngradeReason,
  ],
  ptqs: [
    TythonColumnsEnum.AdUnitName,
    TythonColumnsEnum.AdUnitId,
    TythonColumnsEnum.PTQS,
    TythonColumnsEnum.BiddedSRPVs,
    TythonColumnsEnum.RawSRPVs,
  ],
  adunitsnapshot: [
    TythonColumnsEnum.AccountName,
    TythonColumnsEnum.AdUnitId,
    TythonColumnsEnum.AdUnitName,
    TythonColumnsEnum.SelectTierEligibilityStatus,
    TythonColumnsEnum.SelectTierDowngradeReason,
  ],
  ptqssnapshot: [
    TythonColumnsEnum.Date,
    TythonColumnsEnum.AdUnitName,
    TythonColumnsEnum.PTQSRemediationStatus,
    TythonColumnsEnum.PTQS,
    TythonColumnsEnum.NumberOfWeeks,
  ],
  distribution: [
    TythonColumnsEnum.Date,
    TythonColumnsEnum.DistributionPartnerCode,
    TythonColumnsEnum.FormCode,
    TythonColumnsEnum.EstimatedRevenue,
    TythonColumnsEnum.BiddedSRPVs,
    TythonColumnsEnum.RPM,
  ],
  distributionv2: [
    TythonColumnsEnum.Date,
    TythonColumnsEnum.PartnerCode,
    TythonColumnsEnum.FormCode,
    TythonColumnsEnum.FollowOnType,
    TythonColumnsEnum.ARPV,
    TythonColumnsEnum.BiddedSRPVs,
    TythonColumnsEnum.Clicks,
    TythonColumnsEnum.Revenue,
    TythonColumnsEnum.RPM,
  ],
  distributionptqs: [
    TythonColumnsEnum.Date,
    TythonColumnsEnum.PartnerCode,
    TythonColumnsEnum.AdCountry,
    TythonColumnsEnum.DeviceType,
    TythonColumnsEnum.ImpactScore,
    TythonColumnsEnum.PTQS,
  ],
  distributionpageloadperf: [
    TythonColumnsEnum.Date,
    TythonColumnsEnum.PartnerCode,
    TythonColumnsEnum.RedirectionPhase,
    TythonColumnsEnum.Market,
    TythonColumnsEnum.Device,
    TythonColumnsEnum.P90_Latency_ms,
    TythonColumnsEnum.P90_Latency_ms_7DMA,
  ],
  distributionpartnertagv2: [
    TythonColumnsEnum.Date,
    TythonColumnsEnum.PartnerCode,
    TythonColumnsEnum.PartnerTag,
    TythonColumnsEnum.FormCode,
    TythonColumnsEnum.FollowOnType,
    TythonColumnsEnum.ARPV,
    TythonColumnsEnum.BiddedSRPVs,
    TythonColumnsEnum.Clicks,
    TythonColumnsEnum.Revenue,
    TythonColumnsEnum.RPM,
  ],
  distributionptqssnapshot: [
    TythonColumnsEnum.Date,
    TythonColumnsEnum.PartnerCode,
    TythonColumnsEnum.PTQSRemediationStatus,
    TythonColumnsEnum.PTQS,
    TythonColumnsEnum.NumberOfWeeks,
  ],
  startdistributioncontent: [TythonColumnsEnum.ContentExperience, TythonColumnsEnum.PageViews, TythonColumnsEnum.UniqueUsers],
  startdistributionnativeadsinfeedsandheadlines: [
    TythonColumnsEnum.Date,
    TythonColumnsEnum.Experience,
    TythonColumnsEnum.Market,
    TythonColumnsEnum.AdImpressions,
    TythonColumnsEnum.StartDistributionRpm,
    TythonColumnsEnum.NetRevenue,
    TythonColumnsEnum.TotalPartnerRevenueShare,
  ],
  [CLARITY_REPORT_NAME.toLowerCase()]: [
    TythonColumnsEnum.Date,
    TythonColumnsEnum.AdUnitName,
    TythonColumnsEnum.AdUnitId,
    TythonColumnsEnum.ProjectId,
    TythonColumnsEnum.BiddedSRPVs,
    TythonColumnsEnum.EstimatedRevenue,
    TythonColumnsEnum.ClarityCoverage_PageViewBased,
    TythonColumnsEnum.ClarityCoverage_ClickBased,
  ],
};
